import React from 'react';
import { connect } from 'react-redux';
import { useWindowSize } from 'react-use';
import cn from 'classnames';
import moment from 'moment';
import { bindActionCreators } from 'redux';

import beGetBillingDetails from 'actions/billing/beGetBillingDetails.action';
import beUpdateSalonBillingData from 'actions/billing/beUpdateSalonBillingData.action';
import beUpgradePlan from 'actions/billing/beUpgradePlan.action';
import fetchSalon from 'actions/fetchSalon.action';
import fetchStylistsBySalon from 'actions/stylist/fetchStylistsBySalon.action';
import ICO_CLOSE from 'assets/images/close-button.svg';
import IMG_CARD from 'assets/images/menu/settings-menu/credit-card-purple.svg';
import IMG_ESSENTIAL from 'assets/images/price-plan-1.svg';
import IMG_ADVANCED from 'assets/images/price-plan-2.svg';
import IMG_SUPERCHARGED from 'assets/images/price-plan-3.svg';
import isCardExpired from 'bl/plan/isCardExpired.bl';
import AccountHolderDetails from 'components/billing/AccountHolderDetails.component';
import ClientAccountForm from 'components/billing/ClientAccountForm.component';
import ClientCardForm from 'components/billing/ClientCardForm.component';
import InvoiceTable from 'components/billing/InvoiceTable.component';
import SalonSubscription from 'components/billing/SalonSubscription.component';
import Subscription from 'components/billing/Subscription.component';
import ConfirmationDialog from 'components/dialog/ConfirmationDialog.component';
import Badge from 'components/generic/Badge.component';
import Icon from 'components/generic/Icon.component';
import CurrencySign from 'components/price/CurrencySign.component';
import DELAY from 'constants/delay.const';
import PLAN_NAME from 'constants/plan.const';
import PRICE_PER_STYLIST from 'constants/settings/ncobStaffPrices.const';
import MOBILE_VIEWPORT from 'constants/ui/mobileViewport.const';
import { withTranslation } from 'providers/GILT.provider';
import { withTheme } from 'providers/Theme.provider';
import {
  selectCurrentSalon,
  selectCurrentSalonBetaAnnualSalon,
  selectCurrentSalonBilling,
  selectCurrentSalonDigitalCardAddOn,
  selectCurrentSalonId,
  selectCurrentSalonName,
  selectCurrentSalonNewPricing,
  selectCurrentSalonOnlineBooking,
  selectCurrentSalonPostcode,
  selectCurrentSalonPricingPlan,
  selectCurrentSalonSendSms,
  selectCurrentSalonStylists,
} from 'selectors/currentSalon.selectors';
import {
  selectBillingCardData,
  selectBillingIsFetching,
  selectBillingPlan,
  selectBillingStripeToken,
} from 'selectors/settings.selectors';
import getAnyOrNull from 'utils/accessors/getAnyOrNull.util';
import fromAnyToBool from 'utils/converters/fromAnyToBool.util';
import delayed from 'utils/functions/delayed.util';
import logger from 'utils/logger.util';
import isEmptyObject from 'utils/predicates/object/isEmptyObject.util';
import fixed from 'utils/values/fixed.util';

import { CreateToken, getBillingPlans, GetCustomerInvoices, GetToken } from 'settings/billing/actions';
import AdvancedListComponent from 'settings/billing/components/AdvancedList.component';
import DefaultListComponent from 'settings/billing/components/DefaultList.component';
import EssentialListComponent from 'settings/billing/components/EssentialList.component';
import SuperchargedListComponent from 'settings/billing/components/SuperchargedList.component';
import PricePlanFeatures from 'settings/billing/PricePlanFeatures';
import BetaAnnualModal from 'settings/online-booking/modals/BetaAnnualModal';
import NoBillingModal from 'settings/online-booking/modals/NoBillingModal';

import CN from './BillingLayout.module.scss';

const L = logger('BillingLayout');

const DAC_FOR_ONE_STYLIST = 6;
const PRICE_PER_BOOKING = 0.5;

const BASE_PRICE = Object.freeze({
  essential: 20,
  advanced: 35,
  oldSuper: 195,
  newSuper: 219,
});

const UPDATE_CLIENT_SECRET_ACTION = 'update_card';

const HTML_SUB_TITLE = Object.freeze({
  Essential: 'staffAndBusiness.subAndBilling.pricePlan.essentialSubTitle',
  Advanced: 'staffAndBusiness.subAndBilling.pricePlan.advancedSubTitle',
  Supercharged: 'staffAndBusiness.subAndBilling.pricePlan.superchargedSubTitle',
});

const ResponsiveIcon = ({ isOpen }) => {
  const { width: windowWidth } = useWindowSize();
  const isMobileViewport = windowWidth <= MOBILE_VIEWPORT;
  return (
    <Icon
      tag="BillingLayout__icon"
      variant="arrowFoldout"
      selected={!isOpen}
      unselected={isOpen}
      width={isMobileViewport ? 'xl' : 'l1'}
    />
  );
};

class BillingLayout extends React.Component {
  static defaultProps = { stripeCardData: {} };

  constructor(props) {
    super(props);
    this.state = {
      errorMessage: '',
      alert: false,
      activePlan: this.props.currentSalon && this.props.currentSalon.pricing_plan,
      showModal: false,
      newId: 0,
      newPlanName: '',
      showCardDetails: true,
      showAccountForm: true,
      clientSecret: '',
      showBillingDialog: false,
      isBetaAnnualRendered: false,
      isOpen: false,
    };

    this.foldoutBillingRef = React.createRef();
  }

  componentDidMount() {
    const FTAG = 'componentDidMount()';

    const { salonId, GetCustomerInvoices, fetchStylistsBySalon, getBillingPlans, beGetBillingDetails } = this.props;
    L.unignore(FTAG, 'GetCustomerInvoices:', GetCustomerInvoices(salonId));
    L.unignore(FTAG, 'fetchStylistsBySalon:', fetchStylistsBySalon(salonId));
    L.unignore(FTAG, 'getBillingPlans:', getBillingPlans(salonId));
    L.unignore(FTAG, 'beGetBillingDetails:', beGetBillingDetails({ salonId }));
  }

  // noinspection JSCheckFunctionSignatures
  UNSAFE_componentWillReceiveProps(nextProps) {
    if (nextProps.currentSalon.pricing_plan !== this.props.currentSalon.pricing_plan) {
      this.setState({ activePlan: nextProps.currentSalon.pricing_plan });
    }
  }

  onPlanClick = (newId, newPlanName) => {
    const { hasBilling, hasBetaAnnualSalon } = this.props;

    if (hasBilling && hasBetaAnnualSalon) this.setState({ isBetaAnnualRendered: true });
    if (!hasBilling && !hasBetaAnnualSalon) this.setState({ showBillingDialog: true });
    if (hasBilling && !hasBetaAnnualSalon) this.setState({ showModal: true, newId, newPlanName });
  };

  onConfirmBtnUpgrade = () => {
    const FTAG = 'onConfirmBtnUpgrade()';
    const { beUpgradePlan, fetchSalon, salonId } = this.props;
    const { newId: planId } = this.state;

    L.unignore(FTAG, 'beUpgradePlan:', beUpgradePlan({ planId, salonId })).then(
      () => void L.unignore(FTAG, 'fetchSalon:', fetchSalon(salonId)),
    );
  };

  // plan id can be diff of 1,2,3
  onImageUpload = (name) => {
    switch (name) {
      case 'Essential':
        return IMG_ESSENTIAL;
      case 'Advanced':
        return IMG_ADVANCED;
      case 'Supercharged':
        return IMG_SUPERCHARGED;
      default:
        return IMG_ESSENTIAL;
    }
  };

  setPlanId = () => {
    const name = this.props.currentSalon && this.props.currentSalon.pricing_plan;
    if (name === 'Essential') {
      return 'Essential-id';
    }
    if (name === 'Advanced') {
      return 'Advanced-id';
    }
    if (name === 'Supercharged') {
      return 'Supercharged-id';
    }
    return 'No-plan-id';
  };

  setClassName = (name) => {
    switch (name) {
      case 'Essential':
        return 'Essential';
      case 'Advanced':
        return 'Advanced';
      case 'Supercharged':
        return 'Supercharged';
      default:
        return 'Essential';
    }
  };

  setClientSecret = (value) => {
    this.setState({ clientSecret: value });
  };

  setShowAccountForm = (value) => {
    this.setState({ showCardDetails: false, showAccountForm: value });
  };

  hideError = () => void this.setState({ errorMessage: '' });
  isEmpty = (obj) => (obj === null || obj === undefined ? true : Object.keys(obj).length <= 0);

  showAlert = (tf, error) => {
    this.setState({ alert: tf, errorMessage: error });
    if (tf) {
      setTimeout(() => {
        this.setState({ alert: false });
      }, 3000);
    }
  };

  updateAccountHolderDetails = () => {
    const { salonId, beGetBillingDetails } = this.props;
    L.unignore('updateAccountHolderDetails()', 'beGetBillingDetails:', beGetBillingDetails({ salonId }));
    this.setState({ showCardDetails: true, showAccountForm: true });
  };

  updateAccountDetails = () => {
    const { salonId, beGetBillingDetails } = this.props;
    L.unignore('updateAccountDetails()', 'beGetBillingDetails:', beGetBillingDetails({ salonId }));
    this.setState({ showCardDetails: false, showAccountForm: true });
  };

  updateCardDetails = () => {
    const { salonId, beGetBillingDetails, beUpdateSalonBillingData } = this.props;

    L.unignore(
      'updateCardDetails()',
      'beGetBillingDetails:',
      beGetBillingDetails({ salonId }).then((response) => {
        const name = getAnyOrNull('result.json.name', response);
        const email = getAnyOrNull('result.json.email', response);
        if (!name && !email) return;

        // eslint-disable-next-line promise/no-nesting
        beUpdateSalonBillingData({ salonId, name, email, action: UPDATE_CLIENT_SECRET_ACTION }).then((response) => {
          L.debug(response);
          this.setState({ clientSecret: getAnyOrNull('result.json.client_secret', response) });
          this.setState({ showCardDetails: false, showAccountForm: false });
        });
      }),
    );
  };

  handleGoBack = () => void this.setState({ showCardDetails: true, showAccountForm: true });

  calcPlanPrice = (name) => {
    const { isNewPricing, pricingPlan } = this.props;
    // NOTE:
    // new_pricing: true → salon is on the NEW pricing, which is:
    // Essential £20 + £12 per column
    // Advanced £35 + £18 per column
    // Supercharged £219

    // new_pricing: false → salon is on the OLD pricing, which is:
    // Essential £20 + £10 per column
    // Advanced £35 + £15 per column
    // Supercharged £195

    const byActiveWithColumn = (s) => s.status === 'active' && !s.serves_as_waitlist && s.has_column;
    const filteredStylists = this.props.stylistList.filter(byActiveWithColumn).length;

    const essentialBasePrice = BASE_PRICE.essential;
    const advancedBasePrice = BASE_PRICE.advanced;
    let essenPricePerSty = PRICE_PER_STYLIST.essentialNewPrice;
    let advPricePerSty = PRICE_PER_STYLIST.advNewPrice;
    let superBasePrice = BASE_PRICE.newSuper;

    if (pricingPlan === PLAN_NAME.essential && !isNewPricing) {
      essenPricePerSty = PRICE_PER_STYLIST.essentialOldPrice;
    }
    if (pricingPlan === PLAN_NAME.advanced && !isNewPricing) {
      advPricePerSty = PRICE_PER_STYLIST.advOldPrice;
    }

    if (pricingPlan === PLAN_NAME.supercharged && !isNewPricing) {
      superBasePrice = BASE_PRICE.oldSuper;
    }
    // £35 + number of stylist * £18 - £18 for the first/basic stylist which is the first
    const totalEssentialPrice = essentialBasePrice + filteredStylists * essenPricePerSty - essenPricePerSty;
    const totalAdvancedPrice = advancedBasePrice + filteredStylists * advPricePerSty - advPricePerSty;

    switch (name) {
      case 'Essential':
        if (this.props.isDac) {
          return totalEssentialPrice + DAC_FOR_ONE_STYLIST * filteredStylists;
        }
        return totalEssentialPrice;
      case 'Advanced':
        return totalAdvancedPrice;
      case 'Supercharged':
        return superBasePrice;
      default:
        return essentialBasePrice;
    }
  };

  calcPricePerStaffPerDay = (name) => {
    const { isNewPricing, pricingPlan } = this.props;

    const daysInMonth = moment().daysInMonth();

    const styNumber = this.props.stylistList.filter(
      (stylist) => stylist.status === 'active' && !stylist.serves_as_waitlist && stylist.has_column,
    ).length;

    const essentialBasePrice = BASE_PRICE.essential;
    const advancedBasePrice = BASE_PRICE.advanced;
    let essenPricePerSty = PRICE_PER_STYLIST.essentialNewPrice;
    let advPricePerSty = PRICE_PER_STYLIST.advNewPrice;
    let superBasePrice = BASE_PRICE.newSuper;

    if (pricingPlan === PLAN_NAME.essential && !isNewPricing) {
      essenPricePerSty = PRICE_PER_STYLIST.essentialOldPrice;
    }
    if (pricingPlan === PLAN_NAME.advanced && !isNewPricing) {
      advPricePerSty = PRICE_PER_STYLIST.advOldPrice;
    }

    if (pricingPlan === PLAN_NAME.supercharged && !isNewPricing) {
      superBasePrice = BASE_PRICE.oldSuper;
    }

    const totalEssentialPrice = essentialBasePrice + styNumber * essenPricePerSty - essenPricePerSty;
    const totalAdvancedPrice = advancedBasePrice + styNumber * advPricePerSty - advPricePerSty;

    if (name === 'Essential') {
      if (this.props.isDac) {
        return (totalEssentialPrice + DAC_FOR_ONE_STYLIST * styNumber) / daysInMonth / styNumber;
      }
      return totalEssentialPrice / daysInMonth / styNumber;
    } else if (name === 'Advanced') {
      return totalAdvancedPrice / daysInMonth / styNumber;
    } else if (name === 'Supercharged') {
      return superBasePrice / daysInMonth / styNumber;
    }
    return essentialBasePrice;
  };

  htmlList = (name) => {
    switch (name) {
      case 'Essential':
        return <EssentialListComponent />;
      case 'Advanced':
        return <AdvancedListComponent />;
      case 'Supercharged':
        return <SuperchargedListComponent />;
      default:
        return <DefaultListComponent />;
    }
  };

  htmlInfo = (pricingPlan) => {
    const { isNewPricing, t } = this.props;
    const essenPricePerSty = isNewPricing ? PRICE_PER_STYLIST.essentialNewPrice : PRICE_PER_STYLIST.essentialOldPrice;
    const advPricePerSty = isNewPricing ? PRICE_PER_STYLIST.advNewPrice : PRICE_PER_STYLIST.advOldPrice;

    if (pricingPlan === PLAN_NAME.essential) {
      return (
        <div>
          {t('staffAndBusiness.subAndBilling.pricePlan.additionalChargesText1')} <CurrencySign />
          {essenPricePerSty} {t('staffAndBusiness.subAndBilling.pricePlan.additionalChargesText2')}
        </div>
      );
    } else if (pricingPlan === PLAN_NAME.advanced) {
      return (
        <div>
          {t('staffAndBusiness.subAndBilling.pricePlan.additionalChargesText1')} <CurrencySign />
          {advPricePerSty} {t('staffAndBusiness.subAndBilling.pricePlan.additionalChargesText2')}
        </div>
      );
    } else if (pricingPlan === PLAN_NAME.supercharged) {
      return t('staffAndBusiness.subAndBilling.pricePlan.unlimitedColumnsText');
    }
    return t('staffAndBusiness.subAndBilling.pricePlan.additionalChargesText1');
  };

  handleOpenBilling = () => {
    this.setState({ isOpen: true }, () => {
      delayed(DELAY.fast, () => {
        if (this.foldoutBillingRef?.current) {
          this.foldoutBillingRef?.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });
    });
  };

  handleToggleBilling = () => this.setState({ isOpen: !this.state.isOpen });

  render() {
    const {
      errorMessage,
      stylistList,
      sendSMS,
      billingPlan,
      isFetching,
      onlineBooking,
      stripeCardData,
      salonId,
      salonName,
      beUpdateSalonBillingData,
      pricingPlan,
      isDarkMode,
      t,
    } = this.props;

    const {
      activePlan,
      showCardDetails,
      clientSecret,
      showAccountForm,
      showBillingDialog,
      isBetaAnnualRendered,
      isOpen,
    } = this.state;

    const {
      name,
      email,
      last4_digit: last4Digits,
      expiry_year: expiryYear,
      expiry_month: expiryMonth,
    } = stripeCardData;

    const isNew = isEmptyObject(stripeCardData);
    const isBillingEmpty = fromAnyToBool(isNew);
    const isBillingComplete = fromAnyToBool(!isNew && last4Digits && expiryYear);
    const isBillingIncomplete = fromAnyToBool(!isNew && !last4Digits && !expiryYear);

    const errorMessageDivClassName =
      this.state.errorMessage || errorMessage ? 'input-prompt-show' : 'input-prompt-hide';

    const monthlyPrice = [0, 10, 20, 30];
    const monthlySMSPrice = [5, 8, 12, 15];

    let coreMonthlyCharge;
    let coreMonthlySMSPrice;
    const activeStylistNum = stylistList.filter((stylist) => stylist.status === 'active').length;
    const S = activeStylistNum > 1 ? 's' : '';

    let discount;
    let discountIsPercent;

    let billingTotal;

    switch (activeStylistNum) {
      case 0:
      case 1: {
        [coreMonthlyCharge] = monthlyPrice;
        [coreMonthlySMSPrice] = monthlySMSPrice;
        break;
      }
      case 2:
      case 3: {
        [, coreMonthlyCharge] = monthlyPrice;
        [, coreMonthlySMSPrice] = monthlySMSPrice;
        break;
      }
      case 4:
      case 5:
      case 6: {
        [, , coreMonthlyCharge] = monthlyPrice;
        [, , coreMonthlySMSPrice] = monthlySMSPrice;
        break;
      }
      default: {
        [, , , coreMonthlyCharge] = monthlyPrice;
        [, , , coreMonthlySMSPrice] = monthlySMSPrice;
      }
    }

    if (!sendSMS) {
      coreMonthlySMSPrice = 0;
    }

    let discountValueOrPercent;

    if (discount) {
      discountValueOrPercent = !discountIsPercent ? (
        <div>
          <CurrencySign />
          {discount}
        </div>
      ) : (
        `-${parseFloat(discount * 100)}%`
      );
    } else {
      discountValueOrPercent = 0;
    }

    if (!discountIsPercent) {
      billingTotal = (
        <>
          <CurrencySign />
          {coreMonthlyCharge + coreMonthlySMSPrice - parseFloat(discount)}
        </>
      );
    } else {
      const sub = coreMonthlyCharge + coreMonthlySMSPrice;
      const percent = 1 - discount;
      billingTotal = (
        <>
          <CurrencySign />
          {parseFloat(sub * percent).toFixed(2)}
        </>
      );
    }

    const renderedBillingForm = showAccountForm ? (
      <ClientAccountForm
        beUpdateSalonBillingData={beUpdateSalonBillingData}
        handleGoBack={this.handleGoBack}
        isNew={isNew}
        onChange={this.updateAccountHolderDetails}
        salonId={salonId}
        setClientSecret={this.setClientSecret}
        setShowAccountForm={this.setShowAccountForm}
        stripeEmail={email}
        stripeName={name}
      />
    ) : (
      <ClientCardForm
        clientSecret={clientSecret}
        handleGoBack={this.handleGoBack}
        isNew={isNew}
        isBillingIncomplete={!isEmptyObject(stripeCardData) && !last4Digits && !expiryYear}
        onChange={this.updateAccountHolderDetails}
        salonId={salonId}
        subscriptionId={stripeCardData.subscription_id}
      />
    );

    const displayPlanName = pricingPlan || t('common.custom');
    // const pricePlanFeatures = determinePricePlanFeatures(t);

    const Title = () => {
      const expired = isCardExpired(expiryMonth, expiryYear);

      return (
        <div data-bem="BillingLayout--Title" className={CN.header}>
          <div className={CN.headerTitle}>
            {t('staffAndBusiness.subAndBilling.billingDetails.foldoutTitle')}
            <div className={CN.badgeContainer}>
              {expired && <Badge variant="alert" height="sm" name={t('composite.badge.expired')} />}
              {!expired && isBillingEmpty && <Badge variant="draft" height="sm" name={t('composite.badge.empty')} />}
              {!expired && isBillingIncomplete && (
                <Badge variant="attention" height="sm" name={t('composite.badge.incomplete')} />
              )}
              {!expired && isBillingComplete && (
                <Badge variant="success" height="sm" name={t('composite.badge.complete')} />
              )}
            </div>
          </div>
          {expired && (
            <div className={CN.headerAlert}>
              <Icon variant="errorTriangle" width="lg" />
              {t('staffAndBusiness.subAndBilling.billingDetails.headerAlert')}
            </div>
          )}
        </div>
      );
    };

    return (
      <div
        data-bem="BillingLayout"
        className={cn({
          'billing-layout': true,
          [CN.component]: true,
          [CN.dark]: isDarkMode,
        })}
      >
        <SalonSubscription onButtonClick={this.handleOpenBilling} />

        <InvoiceTable />

        <div className={CN.foldout} ref={this.foldoutBillingRef}>
          <div data-bem="Foldout__header" className={CN.container} onClick={this.handleToggleBilling}>
            <div className={CN.title}>
              <img src={IMG_CARD} alt="Your Subscription" className={CN.icon} /> <Title />
            </div>
            <ResponsiveIcon isOpen={isOpen} />
          </div>
          {isOpen && (
            <div>
              {isNew ? (
                renderedBillingForm
              ) : (
                <div>
                  {showCardDetails ? (
                    <AccountHolderDetails
                      email={email}
                      expiryMonth={expiryMonth}
                      expiryYear={expiryYear}
                      handleUpdateAccountDetails={this.updateAccountDetails}
                      handleUpdateCardDetails={this.updateCardDetails}
                      last4digits={last4Digits}
                      name={name}
                      salonId={salonId}
                      salonName={salonName}
                    />
                  ) : (
                    renderedBillingForm
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        <div data-bem="BillingLayout__pricing-page" className="pricing-page">
          {
            <div className="scroll-container">
              <div data-bem="BillingLayout__pricing-page-plan-layout" id={this.setPlanId()} className="eqWrap">
                <div data-bem="BillingLayout__pricing-page-plan-item-features" className="plan-item eq features">
                  <div className="inner">
                    <img src={IMG_ADVANCED} alt="options" style={{ visibility: 'hidden' }} />
                    <h4 className="title">
                      {t('staffAndBusiness.subAndBilling.pricePlan.title')}
                      <small style={{ visibility: 'hidden' }}>
                        {t('staffAndBusiness.subAndBilling.pricePlan.keepIt')}
                      </small>
                    </h4>

                    <PricePlanFeatures />

                    <div className="price-value">
                      <h2 style={{ visibility: 'hidden' }}>{t('staffAndBusiness.subAndBilling.pricePlan.keepIt')}</h2>
                      <span style={{ visibility: 'hidden' }}>
                        {t('staffAndBusiness.subAndBilling.pricePlan.keepIt')}
                      </span>
                      <small style={{ visibility: 'hidden' }}>
                        {t('staffAndBusiness.subAndBilling.pricePlan.keepIt')}
                      </small>
                    </div>
                    <div className="price-value">
                      <h2 style={{ visibility: 'hidden' }}>{t('staffAndBusiness.subAndBilling.pricePlan.keepIt')}</h2>
                      <span style={{ visibility: 'hidden' }}>
                        {t('staffAndBusiness.subAndBilling.pricePlan.keepIt')}
                      </span>
                      <small style={{ visibility: 'hidden' }}>
                        {t('staffAndBusiness.subAndBilling.pricePlan.keepIt')}
                      </small>
                    </div>
                    <button className="btn btn-violet" style={{ visibility: 'hidden' }} />
                  </div>
                </div>
                {!billingPlan ? (
                  <div style={{ position: 'relative', width: '50%' }}>
                    <div className="spinner-container">
                      <div className="load-spinner" />
                      <span className="spinner-text">{t('common.loading')}</span>
                    </div>
                  </div>
                ) : (
                  billingPlan.map((plan) => {
                    const isActive = plan.name === activePlan;
                    const btnChangePlan = cn({
                      btn: true,
                      'btn-outline active-button': isActive,
                      'btn-violet': !isActive,
                    });
                    const planItem = cn({ 'plan-item eq': true, 'active-plan animated -slow zoomIn': isActive });
                    return (
                      <div
                        data-bem="BillingLayout__pricing-page-plan-item-choice"
                        key={plan.id}
                        id={this.setClassName(plan.name)}
                        className={planItem}
                      >
                        <div className="inner">
                          <div className="most-popular">
                            {t('staffAndBusiness.subAndBilling.pricePlan.mostPopular')}
                          </div>
                          <img src={this.onImageUpload(plan.name)} alt={plan.name} />
                          <h4 className="title">
                            {plan.name}
                            <small>
                              {t(HTML_SUB_TITLE[plan.name]) ||
                                t('staffAndBusiness.subAndBilling.pricePlan.essentialSubTitle')}
                            </small>
                          </h4>

                          {this.htmlList(plan.name)}

                          <div className="price-value">
                            <h2>
                              <div className="prefix">
                                <CurrencySign />
                              </div>
                              {this.calcPlanPrice(plan.name)}
                            </h2>
                            <span> {t('staffAndBusiness.subAndBilling.pricePlan.aMonth')}</span>
                            <small className="purple">{t('staffAndBusiness.subAndBilling.pricePlan.worksOutAs')}</small>
                          </div>
                          <div className="price-value">
                            <h3>
                              <div className="prefix">
                                <CurrencySign />
                              </div>
                              {fixed(this.calcPricePerStaffPerDay(plan.name))}
                            </h3>
                            <span>
                              {t('staffAndBusiness.subAndBilling.pricePlan.perStaff')} <br />
                              {t('staffAndBusiness.subAndBilling.pricePlan.perDay')}
                            </span>
                            <small> {this.htmlInfo(plan.name)}</small>
                          </div>
                          <button
                            id="change-plan"
                            className={btnChangePlan}
                            onClick={() => this.onPlanClick(plan.id, plan.name)}
                          >
                            {isActive
                              ? t('staffAndBusiness.subAndBilling.pricePlan.currentPlanButton')
                              : t('staffAndBusiness.subAndBilling.pricePlan.upgradeButton')}
                          </button>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          }
          {this.state.showModal && (
            <ConfirmationDialog
              title={t('staffAndBusiness.subAndBilling.pricePlan.confirmationModalTitle', {
                planName: this.state.newPlanName,
              })}
              leftButtonText={t('common.buttons.cancelUpp')}
              onLeft={() => {
                this.setState({ showModal: false });
              }}
              rightButtonText={t('staffAndBusiness.subAndBilling.pricePlan.confirmationModalButton')}
              onRight={() => {
                this.setState({ showModal: false });
                this.onConfirmBtnUpgrade();
              }}
              onCancel={() => {
                this.setState({ showModal: false });
              }}
            >
              <p>{t('staffAndBusiness.subAndBilling.pricePlan.confirmationModalMsg')}</p>
            </ConfirmationDialog>
          )}

          <NoBillingModal
            customButtonAction
            isOpen={showBillingDialog}
            onClose={() => void this.setState({ showBillingDialog: false })}
            onClick={() => {
              this.setState({ showBillingDialog: false });
              this.handleOpenBilling();
            }}
          />

          {fromAnyToBool(isBetaAnnualRendered) && (
            <BetaAnnualModal
              planName={displayPlanName}
              isOpen
              onClose={() => void this.setState({ isBetaAnnualRendered: false })}
            />
          )}

          {isFetching && (
            <div className="eqWrap" style={{ position: 'relative' }}>
              <div className="spinner-container">
                <div className="load-spinner" />
                <span className="spinner-text">{t('staffAndBusiness.subAndBilling.pricePlan.loadingPlans')}</span>
              </div>
            </div>
          )}
        </div>

        {this.state.alert && !this.state.errorMessage && (
          <div className="alert success animated fadeInDown" style={{ marginRight: 0 }}>
            {t('staffAndBusiness.subAndBilling.billingDetails.billingToastMsg')}
            <span className="close" onClick={() => this.showAlert(false)} role="button" tabIndex={0}>
              <img src={ICO_CLOSE} alt="Close modal" />
            </span>
          </div>
        )}
        <div className="billing-plan">
          <p className="billing-total">{billingTotal}</p>
          {onlineBooking && (
            <p className="billing-online">
              + <CurrencySign />
              {t('staffAndBusiness.subAndBilling.billingDetails.perOnlineBooking')}
            </p>
          )}
          <p className="billing-note">{t('staffAndBusiness.subAndBilling.billingDetails.perMonthPlusTax')}</p>

          <div className="billing-item-div">
            {stylistList && (
              <p className="billing-item">
                {t('staffAndBusiness.subAndBilling.billingDetails.slickCore', {
                  activeStylist: activeStylistNum,
                  S,
                })}
              </p>
            )}
            <p className="billing-item-value">
              <CurrencySign />
              {coreMonthlyCharge}
            </p>
          </div>
          {sendSMS && (
            <div className="billing-item-div">
              <p className="billing-item">{t('staffAndBusiness.subAndBilling.billingDetails.unlimitedSMSReminders')}</p>
              <p className="billing-item-value">
                <CurrencySign />
                {coreMonthlySMSPrice}
              </p>
            </div>
          )}
          {onlineBooking && (
            <div className="billing-item-div">
              <p className="billing-item">{t('staffAndBusiness.subAndBilling.billingDetails.onlineBooking')}</p>
              <p className="billing-item-value">
                <CurrencySign />
                {t('staffAndBusiness.subAndBilling.billingDetails.perOnlineBooking')}
              </p>
            </div>
          )}
          {parseFloat(discount) > 0 && (
            <div className="billing-item-div">
              <p className="billing-item">{t('staffAndBusiness.subAndBilling.billingDetails.discount')}</p>
              <p className="billing-item-value">{discountValueOrPercent}</p>
            </div>
          )}
          <div className="billing-item-div-bolton">
            <p className="billing-item">{t('staffAndBusiness.subAndBilling.billingDetails.boltOns')}</p>
            {sendSMS && (
              <div className="billing-item-purple">
                <h3>{t('staffAndBusiness.subAndBilling.billingDetails.SmsReminders')}</h3>
                <div className="billing-item-div-purple">
                  <p className="billing-item-small">
                    {t('staffAndBusiness.subAndBilling.billingDetails.fixedMonthlyFeeMsg')}
                  </p>
                  <p className="billing-item-value-purple">
                    <CurrencySign />
                    {coreMonthlySMSPrice} <span>{t('staffAndBusiness.subAndBilling.billingDetails.perMonth')}</span>
                  </p>
                </div>
              </div>
            )}
            {onlineBooking && (
              <div className="billing-item-purple">
                <h3>{t('staffAndBusiness.subAndBilling.billingDetails.onlineBooking')}</h3>
                <div className="billing-item-div-purple">
                  <p className="billing-item-small">
                    {t('staffAndBusiness.subAndBilling.billingDetails.onlineBookingMsg')}
                  </p>
                  <p className="billing-item-value-purple">
                    <CurrencySign />
                    {PRICE_PER_BOOKING} <span>{t('staffAndBusiness.subAndBilling.billingDetails.perBooking')}</span>
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        {errorMessage && <h3 className="errorMessage">{errorMessage}</h3>}

        {errorMessage && errorMessage.indexOf('Not found') < 0 && (
          <div className={errorMessageDivClassName}>
            <div className="input-baseline" />
            <span>{this.state.errorMessage || errorMessage}</span>
          </div>
        )}

        <Subscription />
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  billingPlan: selectBillingPlan(state),
  currentSalon: selectCurrentSalon(state),
  isFetching: selectBillingIsFetching(state),
  onlineBooking: selectCurrentSalonOnlineBooking(state),
  postcode: selectCurrentSalonPostcode(state),
  salonId: selectCurrentSalonId(state),
  salonName: selectCurrentSalonName(state),
  sendSMS: selectCurrentSalonSendSms(state),
  stripeCardData: selectBillingCardData(state),
  stripeToken: selectBillingStripeToken(state),
  stylistList: selectCurrentSalonStylists(state),
  hasBilling: selectCurrentSalonBilling(state),
  hasBetaAnnualSalon: selectCurrentSalonBetaAnnualSalon(state),
  pricingPlan: selectCurrentSalonPricingPlan(state),
  isNewPricing: selectCurrentSalonNewPricing(state),
  isDac: selectCurrentSalonDigitalCardAddOn(state),
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      GetCustomerInvoices,
      CreateToken,
      GetToken,
      fetchStylistsBySalon,
      getBillingPlans,
      beGetBillingDetails,
      beUpdateSalonBillingData,
      beUpgradePlan,
      fetchSalon,
    },
    dispatch,
  );

const BillingLayoutHoc = connect(mapStateToProps, mapDispatchToProps)(withTranslation()(withTheme()(BillingLayout)));

export default BillingLayoutHoc;
